import React, { useRef } from "react";
import { Box, Stack } from "@chakra-ui/react";
import {
  useCollectionHasHighlights,
  useCollectionKey,
  useMainCollectionWorkflowId,
  useProjectParams,
  useTileProps,
  useCollectionMetadataIds,
  useContents,
} from "hooks";
import type { FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";
import { useConversationContext } from "screens/thread/ConversationContext";
import { CollectionStatProgress } from "../../../../collection/components/CollectionStatProgress";
import { TextOverflowTooltip } from "screens/landing/components/TextOverflowTooltip";
import { useWorkflowStatus } from "hooks/useWorkflows";
import { usePausedWorkflowModal } from "screens/collection/views/ProjectActions/PausedWorkflowModal";
import { ProjectHeaderImage } from "../projectLandingTileLayouts/components/ProjectHeaderImage";
import { ProjectDeleteButton } from "screens/collection/views/ProjectActions/ProjectDeleteButton";

interface Props {
  collectionId: string;
}

export const ProjectLandingLegacyTile: FunctionComponent<Props> = ({ collectionId }) => {
  const { projectFilter } = useProjectParams();
  const navigate = useNavigate();
  const { onConversationOpen, onConversationClose, onDeleteProjectModalOpen } = useConversationContext();
  const collectionType = useCollectionKey(collectionId, "collectionType");
  const collectionName = useCollectionKey(collectionId, "name");
  const hasHighlights = useCollectionHasHighlights(collectionId);
  const commonTileProps = useTileProps();
  const tileRef = useRef<HTMLDivElement>(null);
  const mainWorkflowId = useMainCollectionWorkflowId(collectionId);
  const workflowStatus = useWorkflowStatus(mainWorkflowId);
  const { onOpen: onOpenPausedWorkflow } = usePausedWorkflowModal();
  const collectionMetadataIds = useCollectionKey(collectionId, "metadataIds");
  const filteredContentIdsWithoutProjectOutput = useCollectionMetadataIds(collectionId);
  useContents(collectionMetadataIds || [], { refreshFromNetwork: false });

  const getPath = () => {
    if (projectFilter && collectionId) {
      onConversationClose();
      return navigate(`/projects/${projectFilter}/${collectionId}`);
    } else {
      onConversationOpen();
    }
  };

  return (
    <>
      <Box
        ref={tileRef}
        {...commonTileProps}
        py="1rem"
        onClick={() => {
          getPath();
        }}
        overflow="visible"
        textAlign="left"
        className={`ch-collections-tab-collection-tile ch-project-landing-default-tile ${hasHighlights ? "has" : "no"}-highlights
      `}>
        <Stack direction="row" justifyContent="space-between" align="center" width="100%">
          <Stack direction="row" align="center" spacing="1rem" justifyContent={"start"}>
            <Stack width="6rem" align="center">
              <CollectionStatProgress
                {...(workflowStatus === "failed_checkstop" && {
                  onClick: () => {
                    onOpenPausedWorkflow(collectionId);
                  },
                })}
                showTimeDetails
                collectionId={collectionId}
                size="xxs"
              />
            </Stack>
            <ProjectHeaderImage
              width="unset"
              collectionId={collectionId}
              projectName={collectionName || "New Project"}
              metadataIds={filteredContentIdsWithoutProjectOutput}
              projectType={collectionType}
            />
            <TextOverflowTooltip className="ch-project-landing-default-tile-title" label={collectionName || "New Project"} />
          </Stack>
          <ProjectDeleteButton
            label={"Delete Project"}
            onOpenConfirmation={onDeleteProjectModalOpen}
            projectId={collectionId}
            projectType={"project"}
            isIcon
          />
        </Stack>
      </Box>
    </>
  );
};
